/**
 * API base URL from environment.
 * Set VITE_API_URL in .env (e.g. VITE_API_URL=https://api.example.com).
 * When unset, empty string is used so requests are relative to the current origin.
 */
export const apiBaseUrl: string = (import.meta.env.VITE_API_URL as string) ?? ''

/** Martin tile server base URL. Prefer VITE_MARTIN_TILES_URL; fallback to apiBaseUrl + '/martin'. Use as-is (e.g. https://martin.sigcomservices.position.cm) so tile requests hit the real Martin host. */
const RAW_MARTIN = (import.meta.env.VITE_MARTIN_TILES_URL as string) ?? ''
const API_BASE = apiBaseUrl.replace(/\/+$/, '')
const FALLBACK_MARTIN = API_BASE ? `${API_BASE}/martin` : ''
export const martinTilesBaseUrl: string = (RAW_MARTIN || FALLBACK_MARTIN).replace(/\/+$/, '')

/**
 * Resolves a media path (e.g. /media/foo.svg) to an absolute URL using the API base.
 * If the path is already absolute (starts with http), it is returned as-is.
 */
export function resolveMediaUrl(path: string): string {
  if (!path) return ''
  if (path.startsWith('http')) return path
  return API_BASE + path
}

/**
 * Prefer API thumbnail when present; otherwise contentUrl. Returns an absolute URL or ''.
 */
export function resolveMediaObjectDisplayUrl(
  media:
    | { contentUrl?: string; contentsFilteredUrl?: { thumbnail?: string } }
    | null
    | undefined,
): string {
  if (media == null) return ''
  const thumb = media.contentsFilteredUrl?.thumbnail
  const path =
    typeof thumb === 'string' && thumb.trim() !== '' ? thumb : (media.contentUrl ?? '').trim()
  if (path === '') return ''
  return resolveMediaUrl(path)
}

/** Maki sprite URL served by Martin. Used at map init to enable OSM icon layers (symbol type). */
export const martinSpriteUrl: string = martinTilesBaseUrl ? `${martinTilesBaseUrl}/sprite/maki` : ''

/**
 * When true, do not call GET /api/resources|projects/filter_options; facet lists are built from the collection only.
 * Set in .env while those endpoints return 5xx; omit or set false when the API is fixed.
 */
export const skipPlatformFilterOptionsEndpoint: boolean =
  String(import.meta.env.VITE_SKIP_PLATFORM_FILTER_OPTIONS ?? '').toLowerCase() === 'true'

/**
 * Public site origin for geoportail deep links when leaving the SPA (e.g. home header search).
 * Set VITE_GEOPORTAIL_PUBLIC_URL=https://sigcom.position.cm in production if the app is hosted elsewhere.
 * When unset, the current browser origin is used (local dev and same-host deploys).
 */
export function getGeoportailPublicOriginForDeeplink(): string {
  const fromEnv = (import.meta.env.VITE_GEOPORTAIL_PUBLIC_URL as string | undefined)
    ?.trim()
    .replace(/\/+$/, '')
  if (fromEnv) return fromEnv
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin
  return ''
}

/** Absolute URL to open the geoportail for a commune (e.g. https://host/geoportail?commune=Tibati). */
export function getGeoportailCommuneAbsoluteUrl(
  communeName: string,
  extraQuery: Record<string, string> = {},
): string {
  const origin = getGeoportailPublicOriginForDeeplink()
  const path = '/geoportail'
  const query = new URLSearchParams({ commune: communeName.trim(), ...extraQuery }).toString()
  return `${origin}${path}?${query}`
}
